import { Component, OnDestroy, OnInit } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { AuthService } from '../../singleton-services/auth/auth.service'
import { VerticalService } from '../../singleton-services/vertical.service'
import { combineLatest, iif, Observable, of } from 'rxjs'
import { map, mergeMap, tap, take, switchMap } from 'rxjs/operators'
import { FunnelHeaderPromptStatus } from '../shared/funnel-checkout-header/funnel-checkout-header.component'
import { ILandingPageComponent } from '../landing-page/components/landing-page-legacy/landing-page-legacy.component'
import { BatmanDataService, carouselBackgroundGen } from '../landing-page/batman-data.service'
import { NodeService } from '../../shared/services/node.service'
import { getLiveSiteTitle, LiveEventDescriptionDeps, makeLiveEventDescription } from './signup.utility'
import { FloDatePipe } from '../../shared/pipes/flodate.pipe'
import { UpdateHeadService } from '../../singleton-services/seo/update-head.service'
import {
  buildCanonicalUrl,
  buildLiveEventSchema,
  CANONICAL_ID,
  STRUCTURED_LIVE_EVENT_SCHEMA_ID,
  addSlugToUrl,
  createGenericCanonicalUrl
} from '../../singleton-services/seo/seo.utility'
import { SignupLiveEventService } from './signup-live-event.service'
import { EVENT_SCHEMA_ID, makeEventHeadElementData } from '../../singleton-services/seo/event.utility'
import { SeoService } from 'src/app/singleton-services/seo/seo.service'
import type { LiveEvent } from '@flocasts/flosports30-types/dist/entity'
import { SiteSettings } from 'src/app/shared/models/site-settings.model'
import { LiveEventStatus } from '../../events/events.enum'
import { GeoBlockingService } from 'src/app/singleton-services/geo-blocking.service'
import { PlatformService } from 'src/app/singleton-services/platform.service'
import { ExperimentationService } from 'src/app/shared/experimentation/experimentation.service'
import {
  FEAT_NEW_FUNNEL_LANDING_PAGE_ID,
  FEAT_NEW_FUNNEL_LANDING_PAGE_VARIATION_1,
  FEAT_NEW_FUNNEL_LANDING_PAGE_VARIATION_2,
  FEAT_PRICING_ARCH_ID,
  FEAT_PRICING_ARCH_VARIATION
} from 'src/app/shared/experimentation/experiment.model'
import { CheckoutNavigationService } from 'src/app/shared/services/checkout-navigation.service'
import { OffersService } from 'src/app/singleton-services/offers.service'
import { OfferSelectionCardModel } from '@flocasts/experience-service-types'
import { ProductService } from 'src/app/shared/services/product.service'
import { mapPlansToOfferSelectionCardModel } from '../plans/legacy-plans/legacy-plans.utility'
import { Plan } from '@flocasts/flosports30-types/dist/subscriptions'

@Component({
  selector: 'flo-signup',
  templateUrl: './signup.component.html',
  styleUrls: ['./signup.component.scss']
})
export class SignupComponent implements OnDestroy, OnInit {
  public readonly liveEvent: LiveEvent = this.ar.snapshot.data && this.ar.snapshot.data.liveEvent

  private readonly eventAssetUrl$: Observable<string | undefined> = this.ar.data.pipe(
    tap(data => {
      this.signupLiveEventService.updateLiveEvent(data.liveEvent)
    }),
    mergeMap(data => (data.liveEvent ? this.signupLiveEventService.eventHubAsset$ : of(undefined)))
  )

  private updateMetaTagsForLiveEvent(siteSettings: SiteSettings, assetUrl: string | undefined): void {
    const title = getLiveSiteTitle(this.liveEvent)?.replace('<br>', ' ')
    this.nodeService.setTitle(`${title} - ${siteSettings.site_name}`)
    const descriptionDeps: LiveEventDescriptionDeps = {
      title: this.liveEvent.title,
      status: this.liveEvent.status,
      start_date_time: this.datePipe.transform(this.liveEvent.start_date_time) as string,
      start_at_next: this.liveEvent.start_at_next,
      start_time: this.liveEvent.start_date_time
    }
    const description = makeLiveEventDescription(descriptionDeps, siteSettings.site_name as string)
    this.nodeService.setDescription(description)
    if (assetUrl !== undefined) this.nodeService.setImage(assetUrl)
    const canonical = this.liveEvent.live_event_url
      ? buildCanonicalUrl(addSlugToUrl(this.liveEvent.live_event_url, this.liveEvent.slug))
      : undefined
    if (canonical !== undefined) {
      this.updateHeadService.addElementToHead(canonical)
    }
  }

  private updateSchemaForLiveEvent(siteSettings: SiteSettings, assetUrl?: string): void {
    const asset = assetUrl ? { url: assetUrl } : undefined
    const liveEventSchema = buildLiveEventSchema({
      ...this.liveEvent,
      asset,
      siteName: siteSettings && siteSettings.site_name
    })
    this.updateHeadService.addElementToHead(liveEventSchema)
    const eventHeadElement = makeEventHeadElementData(this.liveEvent, siteSettings, this.router, assetUrl)
    this.updateHeadService.addElementToHead(eventHeadElement)
  }

  public readonly landingPageProps$: Observable<ILandingPageComponent> = combineLatest([
    this.vs.siteSettings$,
    this.auth.user$,
    this.eventAssetUrl$
  ]).pipe(
    switchMap(([siteSettings, user, assetUrl]) => {
      if (this.liveEvent) {
        this.updateMetaTagsForLiveEvent(siteSettings, assetUrl)
        this.updateSchemaForLiveEvent(siteSettings, assetUrl)

        if (this.platformService.isBrowser) {
          return this.geoBlockingService.getGeoBlockedLiveEvents([this.liveEvent?.id]).pipe(
            map(getIpResponseItem => {
              const isGeoBlocked = getIpResponseItem.length > 0
              return { siteSettings, user, assetUrl, isGeoBlocked }
            })
          )
        }
      }

      return of({ siteSettings, user, isGeoBlocked: false })
    }),
    map(({ siteSettings, user, isGeoBlocked }) => {
      const liveSiteTitle = getLiveSiteTitle(this.liveEvent, isGeoBlocked)
      return {
        isLiveFunnel: !!this.liveEvent,
        liveEventHeader: !!liveSiteTitle
          ? { title: liveSiteTitle, subtitle: isGeoBlocked ? 'See FAQ section below to learn why' : null }
          : undefined,
        siteSettings,
        showLoginPrompt: user.loggedIn ? FunnelHeaderPromptStatus.HIDDEN : FunnelHeaderPromptStatus.SHOW_LOGIN,
        liveEventStatus: this.liveEvent && this.liveEvent.status,
        liveEventRedirect: this.liveEvent
          ? {
              status: this.liveEvent.status as unknown as LiveEventStatus, // TODO: stop using FloSports30Types
              eventId: this.liveEvent.event_ids[0]
            }
          : undefined
      }
    })
  )

  // TODO: Remove this when the new funnel landing page is fully released
  // Replace it with new endpoint call to get live event header data
  public readonly liveEventHeader$ = this.landingPageProps$.pipe(
    map((props: ILandingPageComponent) => props.liveEventHeader)
  )

  private canonicalURL$ = this.vs.siteSettings$.pipe(
    map((siteSettings: SiteSettings) => createGenericCanonicalUrl('signup', siteSettings, this.liveEvent)),
    take(1)
  )

  constructor(
    private readonly ar: ActivatedRoute,
    private readonly auth: AuthService,
    private readonly batmanDataService: BatmanDataService,
    private readonly checkoutNavigationService: CheckoutNavigationService,
    private readonly experimentService: ExperimentationService,
    private readonly datePipe: FloDatePipe,
    private readonly geoBlockingService: GeoBlockingService,
    private readonly nodeService: NodeService,
    private readonly offersService: OffersService,
    private readonly platformService: PlatformService,
    private readonly productService: ProductService,
    private readonly router: Router,
    private readonly seoService: SeoService,
    private readonly signupLiveEventService: SignupLiveEventService,
    private readonly updateHeadService: UpdateHeadService,
    private readonly vs: VerticalService
  ) {}

  public carouselData$ = this.batmanDataService.carouselData$
  public carouselGen = carouselBackgroundGen

  public isNewFunnelLandingVariation1$ = this.experimentService.isInVariation(
    FEAT_NEW_FUNNEL_LANDING_PAGE_ID,
    FEAT_NEW_FUNNEL_LANDING_PAGE_VARIATION_1,
    true
  )

  public isNewFunnelLandingVariation2$ = this.experimentService.isInVariation(
    FEAT_NEW_FUNNEL_LANDING_PAGE_ID,
    FEAT_NEW_FUNNEL_LANDING_PAGE_VARIATION_2,
    true
  )

  public isNewFunnelLandingVariation$ = combineLatest([
    this.isNewFunnelLandingVariation1$,
    this.isNewFunnelLandingVariation2$
  ]).pipe(map(([variation1, variation2]) => variation1 || variation2))

  // new funnel landing page logic starts here
  public readonly siteSettings$ = this.vs.siteSettings$
  public heroContent$ = this.batmanDataService.landingPageContent$.pipe(
    map(landingPageContent => landingPageContent?.heroContent)
  )

  public logoContent$ = this.batmanDataService.landingPageContent$.pipe(
    map(landingPageContent => landingPageContent?.logoContent)
  )

  legacyPlans: Plan[] = []
  /**
   * @deprecated only used for verticals on the old infrastructure
   */
  plans$: Observable<OfferSelectionCardModel[]> = combineLatest([this.productService.plans$, this.siteSettings$]).pipe(
    map(([planData, siteSettings]) => {
      this.legacyPlans = [...planData.plans]
      return mapPlansToOfferSelectionCardModel(planData.plans, siteSettings)
    })
  )

  offers$: Observable<OfferSelectionCardModel[]> = this.experimentService
    .isInVariation(FEAT_PRICING_ARCH_ID, FEAT_PRICING_ARCH_VARIATION)
    .pipe(
      mergeMap((isInNewPricingArchitecture: boolean) => {
        return iif(() => isInNewPricingArchitecture, this.offersService.offers$, this.plans$)
      })
    )

  handleOfferSelected(offer: OfferSelectionCardModel): void {
    if (this.legacyPlans.length > 0) {
      const selectedPlan = this.legacyPlans?.find(plan => plan.id === Number(offer.id))
      this.productService.updateSelectedPlan(selectedPlan)
    } else {
      this.offersService.updateSelectedOffer(offer)
    }

    this.checkoutNavigationService.navigateAfterLandingPage({ redirect: '/pay' }, false)
  }

  handleLoginButtonClicked() {
    this.ar.queryParams
      .pipe(
        take(1),
        tap(queryParams => {
          const liveEvent = this.liveEvent
            ? { status: this.liveEvent.status as unknown as LiveEventStatus, eventId: this.liveEvent.event_ids[0] }
            : undefined
          this.checkoutNavigationService.navigateToLogin(queryParams, liveEvent)
        })
      )
      .subscribe()
  }

  handleSignUpButtonClicked() {
    this.ar.queryParams
      .pipe(
        take(1),
        tap(queryParams => {
          // if the landing page is a live event, we need to pass the live event status and id to the checkout navigation service
          const liveEvent = this.liveEvent
            ? { status: this.liveEvent.status as unknown as LiveEventStatus, eventId: this.liveEvent.event_ids[0] }
            : undefined
          this.checkoutNavigationService.navigateAfterLandingPage(queryParams, false, liveEvent)
        })
      )
      .subscribe()
  }

  public ngOnInit(): void {
    this.canonicalURL$.pipe(take(1)).subscribe((url: string) => {
      this.seoService.setCanonicalUrl(url)
    })
  }

  public ngOnDestroy(): void {
    this.updateHeadService.removeElement(STRUCTURED_LIVE_EVENT_SCHEMA_ID)
    this.updateHeadService.removeElement(EVENT_SCHEMA_ID)
    this.updateHeadService.removeElement(CANONICAL_ID)
    this.nodeService.clearImage()
  }
}
